import * as THREE from 'three';
import { toonMat, createToonGradientMap } from './materials.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MobilityTriggerKind = 'bouncer' | 'launch_pad' | 'rift';

export interface MobilityTriggerResult {
  kind: MobilityTriggerKind;
  impulse: THREE.Vector3;
  triggerGlider: boolean;
}

// ---------------------------------------------------------------------------
// Internal pad/rift descriptors
// ---------------------------------------------------------------------------

interface PadEntry {
  kind: MobilityTriggerKind;
  position: THREE.Vector3;
  group: THREE.Group;
  cooldownUntil: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TRIGGER_RADIUS = 1.8;   // horizontal distance to trigger
const TRIGGER_Y_MAX = 2.5;    // player must be near ground
const COOLDOWN_S = 1.0;

const BOUNCER_BOOST_Y = 14;
const BOUNCER_HORIZ = 2;
const LAUNCH_BOOST_Y = 22;
const RIFT_BOOST_Y = 80;

const RIFT_PARTICLE_COUNT = 16;
const RIFT_ORBIT_RADIUS = 1.8;
const ZIPLINE_MOUNT_PITCH = -0.058;
const ZIPLINE_DISMOUNT_PITCH = 0.072;
const ZIPLINE_NUDGE_LERP = 14;

// ---------------------------------------------------------------------------
// FpsMobilityProps
// ---------------------------------------------------------------------------

export class FpsMobilityProps {
  private readonly scene: THREE.Scene;
  private readonly grad = createToonGradientMap();
  private readonly pads: PadEntry[] = [];

  // Rift per-pad animation state
  private readonly riftRotations: number[] = [];
  private readonly riftParticleAngles: number[][] = [];

  // Zipline mount/dismount camera pitch nudge (lerped each frame)
  private ziplineCamPitch = 0;
  private ziplineCamTarget = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Bouncers (orange pads)
    this.addBouncer(-10, 15);
    this.addBouncer(22, -25);
    this.addBouncer(-30, -35);

    // Launch pads (blue pads)
    this.addLaunchPad(5, -30);
    this.addLaunchPad(-20, 20);

    // Rifts (purple portals)
    this.addRift(0, 0);
    this.addRift(40, -30);
  }

  // ---------------------------------------------------------------------------
  // Public placement API
  // ---------------------------------------------------------------------------

  addBouncer(x: number, z: number): void {
    const group = new THREE.Group();
    group.position.set(x, 0, z);

    // Base disc
    const discGeo = new THREE.CylinderGeometry(1.2, 1.2, 0.2, 12);
    const discMat = toonMat(0xff6a00, this.grad, { emissive: 0xff4400, emissiveIntensity: 0.55 });
    const disc = new THREE.Mesh(discGeo, discMat);
    disc.position.y = 0.1;
    disc.castShadow = true;
    disc.receiveShadow = true;
    group.add(disc);

    // 4 orange chevron marks (thin boxes rotated to form "V" shapes)
    const chevMat = toonMat(0xffaa00, this.grad, { emissive: 0xff8800, emissiveIntensity: 0.9 });
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2;
      for (const side of [-1, 1]) {
        const bar = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.22, 0.5), chevMat);
        bar.position.set(
          Math.cos(angle) * 0.65,
          0.21,
          Math.sin(angle) * 0.65,
        );
        bar.rotation.y = angle + side * 0.45;
        group.add(bar);
      }
    }

    this.scene.add(group);
    this.pads.push({ kind: 'bouncer', position: new THREE.Vector3(x, 0, z), group, cooldownUntil: 0 });
  }

  addLaunchPad(x: number, z: number): void {
    const group = new THREE.Group();
    group.position.set(x, 0, z);

    // Main trampoline disc
    const discMat = toonMat(0x4433ff, this.grad, { emissive: 0x8844ff, emissiveIntensity: 0.6 });
    const disc = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.5, 0.15, 16), discMat);
    disc.position.y = 0.075;
    disc.castShadow = true;
    disc.receiveShadow = true;
    group.add(disc);

    // Concentric ring glow on top face
    const ringMat = toonMat(0xaabbff, this.grad, { emissive: 0xaabbff, emissiveIntensity: 1.0 });
    for (let r = 0; r < 3; r++) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.45 + r * 0.35, 0.03, 4, 20),
        ringMat,
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 0.16;
      group.add(ring);
    }

    // Rim border
    const rimMat = toonMat(0x221188, this.grad, { emissive: 0x4400cc, emissiveIntensity: 0.4 });
    const rim = new THREE.Mesh(new THREE.TorusGeometry(1.5, 0.07, 6, 24), rimMat);
    rim.rotation.x = Math.PI / 2;
    rim.position.y = 0.16;
    group.add(rim);

    this.scene.add(group);
    this.pads.push({ kind: 'launch_pad', position: new THREE.Vector3(x, 0, z), group, cooldownUntil: 0 });
  }

  addRift(x: number, z: number): void {
    const group = new THREE.Group();
    // Rifts float at eye-ish height
    group.position.set(x, 1.2, z);

    // Outer torus ring
    const torusMat = toonMat(0x9933ff, this.grad, { emissive: 0x6600cc, emissiveIntensity: 0.85 });
    const torus = new THREE.Mesh(new THREE.TorusGeometry(1.4, 0.2, 8, 24), torusMat);
    group.add(torus);

    // Inner portal plane
    const portalMat = new THREE.MeshBasicMaterial({
      color: 0x330066,
      transparent: true,
      opacity: 0.72,
      side: THREE.DoubleSide,
    });
    const portal = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 2.4), portalMat);
    group.add(portal);

    // Second iridescent plane (slightly offset for depth shimmer)
    const shimmerMat = new THREE.MeshBasicMaterial({
      color: 0x4400aa,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
    });
    const shimmer = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 2.2), shimmerMat);
    shimmer.position.z = 0.04;
    group.add(shimmer);

    // Particle ring (16 small spheres orbiting)
    const partMat = toonMat(0xcc88ff, this.grad, { emissive: 0xaa44ff, emissiveIntensity: 1.0 });
    const partGeo = new THREE.SphereGeometry(0.07, 4, 4);
    const initialAngles: number[] = [];
    for (let i = 0; i < RIFT_PARTICLE_COUNT; i++) {
      const angle = (i / RIFT_PARTICLE_COUNT) * Math.PI * 2;
      initialAngles.push(angle);
      const p = new THREE.Mesh(partGeo, partMat);
      p.position.set(
        Math.cos(angle) * RIFT_ORBIT_RADIUS,
        Math.sin(angle) * RIFT_ORBIT_RADIUS * 0.1,
        Math.sin(angle) * RIFT_ORBIT_RADIUS,
      );
      group.add(p);
    }

    this.scene.add(group);
    const riftIndex = this.riftRotations.length;
    this.riftRotations.push(0);
    this.riftParticleAngles.push(initialAngles);

    // store rift group index so we can look it up during update
    (group as THREE.Group & { _riftIndex: number })._riftIndex = riftIndex;

    this.pads.push({ kind: 'rift', position: new THREE.Vector3(x, 0, z), group, cooldownUntil: 0 });
  }

  /** Brief camera pitch nudge when attaching to a zipline. */
  triggerZiplineMountNudge(): void {
    this.ziplineCamTarget = ZIPLINE_MOUNT_PITCH;
  }

  /** Brief camera pitch nudge when detaching from a zipline. */
  triggerZiplineDismountNudge(): void {
    this.ziplineCamTarget = ZIPLINE_DISMOUNT_PITCH;
  }

  /** Lerp zipline camera nudge toward target, then back to neutral. */
  updateCameraNudges(dt: number): number {
    this.ziplineCamPitch += (this.ziplineCamTarget - this.ziplineCamPitch) * Math.min(1, ZIPLINE_NUDGE_LERP * dt);
    if (Math.abs(this.ziplineCamPitch - this.ziplineCamTarget) < 0.002) {
      this.ziplineCamTarget *= -0.35;
      if (Math.abs(this.ziplineCamTarget) < 0.004) {
        this.ziplineCamTarget = 0;
      }
    }
    if (Math.abs(this.ziplineCamPitch) < 0.001 && this.ziplineCamTarget === 0) {
      return 0;
    }
    return this.ziplineCamPitch;
  }

  // ---------------------------------------------------------------------------
  // Update — called each frame; returns a trigger result if the player hit a pad
  // ---------------------------------------------------------------------------

  update(dt: number, playerPos: THREE.Vector3): MobilityTriggerResult | null {
    const now = performance.now() * 0.001;

    // Animate rifts
    let riftSeenCount = 0;
    for (const pad of this.pads) {
      if (pad.kind !== 'rift') continue;
      const g = pad.group as THREE.Group & { _riftIndex?: number };
      const ri = g._riftIndex ?? riftSeenCount;
      riftSeenCount++;

      // Spin the whole group
      this.riftRotations[ri] = (this.riftRotations[ri] ?? 0) + 1.5 * dt;
      pad.group.rotation.y = this.riftRotations[ri];

      // Animate particle orbit (draw-in when player is near)
      const dxRift = playerPos.x - pad.position.x;
      const dzRift = playerPos.z - pad.position.z;
      const distRift = Math.sqrt(dxRift * dxRift + dzRift * dzRift);
      const pullFactor = distRift < 8 ? 1 - distRift / 8 : 0; // 0..1 as player closes in
      const orbitR = RIFT_ORBIT_RADIUS * (1 - pullFactor * 0.45);

      const angles = this.riftParticleAngles[ri];
      const children = pad.group.children;
      // particles start at child index 3 (torus, portal, shimmer = 0,1,2)
      for (let i = 0; i < RIFT_PARTICLE_COUNT; i++) {
        angles[i] += (0.9 + pullFactor * 1.4) * dt;
        const child = children[3 + i] as THREE.Mesh | undefined;
        if (!child) continue;
        child.position.set(
          Math.cos(angles[i]) * orbitR,
          Math.sin(angles[i] * 2) * 0.18,
          Math.sin(angles[i]) * orbitR,
        );
      }
    }

    // Collision check — player must be near ground
    if (playerPos.y > TRIGGER_Y_MAX) return null;

    for (const pad of this.pads) {
      if (now < pad.cooldownUntil) continue;

      const dx = playerPos.x - pad.position.x;
      const dz = playerPos.z - pad.position.z;
      const dist2 = dx * dx + dz * dz;
      if (dist2 > TRIGGER_RADIUS * TRIGGER_RADIUS) continue;

      pad.cooldownUntil = now + COOLDOWN_S;

      let impulse: THREE.Vector3;
      let triggerGlider = false;

      switch (pad.kind) {
        case 'bouncer': {
          // Small horizontal push away from pad center + big Y
          const horizMag = dist2 > 0.01 ? BOUNCER_HORIZ / Math.sqrt(dist2) : 0;
          impulse = new THREE.Vector3(dx * horizMag, BOUNCER_BOOST_Y, dz * horizMag);
          triggerGlider = false;
          break;
        }
        case 'launch_pad': {
          // Pure upward blast + glider redeploy
          impulse = new THREE.Vector3(0, LAUNCH_BOOST_Y, 0);
          triggerGlider = true;
          this._playLaunchWhoosh();
          break;
        }
        case 'rift': {
          // Teleport straight up 80 units + glider
          impulse = new THREE.Vector3(0, RIFT_BOOST_Y, 0);
          triggerGlider = true;
          break;
        }
      }

      return { kind: pad.kind, impulse: impulse!, triggerGlider };
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Web Audio whoosh for launch pad
  // ---------------------------------------------------------------------------

  private _audioCtx: AudioContext | null = null;

  private _playLaunchWhoosh(): void {
    try {
      if (!this._audioCtx) {
        this._audioCtx = new AudioContext();
      }
      const ctx = this._audioCtx;
      const dur = 0.55;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(180, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(900, ctx.currentTime + dur * 0.3);
      osc.frequency.linearRampToValueAtTime(1600, ctx.currentTime + dur);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + dur);
    } catch {
      // Silently ignore if Web Audio is unavailable
    }
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  dispose(): void {
    for (const pad of this.pads) {
      this.scene.remove(pad.group);
    }
    this.pads.length = 0;
  }
}
